import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AccountBalance, formattedCurrency, getCurrencySign } from 'vopay';
import { useBlockingLoader } from 'shared';
import usePartnerAccountTransfer, {
  PartnerAccountTransferParams,
} from '@/components/Entities/hooks/useApi/usePartnerAccountTransfer';
import { useAlertDialog } from '@/providers/AlertDialog.provider';
import { usePartnerData } from '@/providers/PartnerData.provider';
import { useToast } from '@/providers/Toast.provider';
import useTransferFormConfig, { TransferFormValues } from './useTransferFormConfig';

/** The `id` of the form element, so actions rendered outside it (a drawer footer) can still submit it. */
export const TRANSFER_FORM_ID = 'transfer-form';

type UseTransferFormProps = {
  /** Fetches the pickers' accounts only while true, so a closed drawer costs nothing. */
  isActive: boolean;
  /** Preselects the From account (a row's Transfer button); absent = start blank. */
  initialFromAccount?: AccountBalance;
  /** After the user acknowledges the success dialog; the Accounts page refreshes its list and closes the drawer. */
  onTransferred?: () => void;
};

/**
 * The transfer flow (spec §2): fill in the transfer, review it with every field locked, then submit.
 * The drawer on the Accounts page and the standalone Transfer page both run this one flow; they only
 * decide where `TransferForm` and `TransferFormActions` go.
 */
export default function useTransferForm({ isActive, initialFromAccount, onTransferred }: UseTransferFormProps) {
  const { t } = useTranslation('translations', { keyPrefix: 'PartnerAccountTransfer' });
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global' });
  const alertDialog = useAlertDialog();
  const toast = useToast();
  const blockingLoader = useBlockingLoader();
  const { transferPartnerAccountFunds } = usePartnerAccountTransfer();
  const [isReviewing, setIsReviewing] = useState(false);

  const {
    response: { isRoleViewOnly },
  } = usePartnerData();

  // The same submit serves both steps: validation has passed by the time it runs, so the first
  // pass locks the form for review and the second sends the transfer.
  async function handleSubmit(values: PartnerAccountTransferParams, formValues: TransferFormValues) {
    if (!isReviewing) {
      setIsReviewing(true);

      return;
    }

    blockingLoader.show();

    try {
      await transferPartnerAccountFunds(values);
    } catch (error) {
      // The hook has logged the cause. Back to the editable form so the input can be corrected.
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';

      setIsReviewing(false);
      toast.neutral(t('error', { defaultValue: 'Could not complete the transfer: {{errorMessage}}', errorMessage }));

      return;
    } finally {
      blockingLoader.hide();
    }

    const fromAccount = getFromAccount(formValues);
    const toAccount = getToAccount(formValues);

    await alertDialog.success(
      t('successTitle', { defaultValue: 'Transfer Completed' }),
      t('successMessage', {
        defaultValue: '{{amount}} was transferred from {{from}} to {{to}}.',
        amount: `${values.Currency} ${getCurrencySign(values.Currency)}${formattedCurrency(values.Amount)}`,
        from: fromAccount?.AccountName ?? values.DebitorAccountID,
        to: toAccount?.AccountName ?? values.RecipientAccountID,
      }),
      { confirmLabel: tGlobal('Buttons.okay') },
    );

    ctx.resetForm();
    setIsReviewing(false);
    // The page keeps this form; its next transfer must see the balances the one just made left.
    refreshAccounts();
    onTransferred?.();
  }

  const { ctx, renderFormElements, getFromAccount, getToAccount, refreshAccounts } = useTransferFormConfig({
    onSubmit: handleSubmit,
    isReviewing,
    isActive,
    initialFromAccount,
  });

  return { ctx, renderFormElements, isReviewing, isRoleViewOnly, backToEdit: () => setIsReviewing(false) };
}

export type TransferFlow = ReturnType<typeof useTransferForm>;
